import { useOrderCheckout } from '@/stores/order-checkout'
import {
	canReopenAt,
	getCheckoutRedirectStep,
	orderSteps,
	type OrderStep,
} from '@/utils/order-checkout'

export function useCheckoutSummary(steps: readonly OrderStep[] = orderSteps) {
	const route = useRoute()
	const orderCheckout = useOrderCheckout()

	// Find current index based on route path match
	const currentStepIndex = computed(() => {
		return steps.findIndex((step) => route.path.includes(step))
	})

	// Get the previous step dynamically
	const previousStep = computed(() => {
		// Guard: Ensure we are past the first step
		if (currentStepIndex.value <= 0) return null

		const currentStep = steps[currentStepIndex.value]
		const prevStepName = steps[currentStepIndex.value - 1]

		// Guard: Ensure both current and previous step names exist
		if (!currentStep || !prevStepName) return null

		return route.path.replace(currentStep, prevStepName)
	})

	// Returns true ONLY if current step is PAST the target step
	const isPast = (targetStep: OrderStep) => {
		const targetIndex = steps.indexOf(targetStep)
		if (targetIndex === -1) return false
		return currentStepIndex.value > targetIndex
	}

	// Returns true ONLY if the step is currently ACTIVE
	const isActive = (targetStep: OrderStep) => {
		return currentStepIndex.value === steps.indexOf(targetStep)
	}

	// Whether going to the step reopens the confirmed order first
	const isReopenable = (targetStep: OrderStep) =>
		orderCheckout.checkout !== null &&
		canReopenAt(orderCheckout.checkout, targetStep)

	// Disable the current step, steps the presale can't open or reopen, and every step while it reopens
	const isDisabled = (targetStep: OrderStep) => {
		if (
			isActive(targetStep) ||
			!orderCheckout.checkout ||
			orderCheckout.isReopening
		)
			return true

		if (isReopenable(targetStep)) return false

		return (
			getCheckoutRedirectStep(orderCheckout.checkout, targetStep) !== null
		)
	}

	return {
		previousStep,
		isPast,
		isActive,
		isDisabled,
		isReopenable,
	}
}
